import React, { useState } from 'react';
import { Badge } from '../../atoms/Badge';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { AppState } from '../../../redux/modules/store';
import { Collapse, Icon } from '@material-ui/core';
import Button from '../../atoms/Button';
import { actions } from '../../../redux/modules/Actions';
import { StoreAndAgencyInfo, StoreSelectorState } from '../../../redux/reducers/StoreSelectorReducer';
import { getCompanyCfcDisplayName } from '../../../Common';
import { getAgencyStoreShortName } from '../FilterSettingDrawer/function';

const SORT_FIELDS = ['kanaName', 'longName', 'shortName', 'companyCfc'] as const;

const LinkedDataStoreSelectModal = () => {
  const isEmployeeModule = process.env.REACT_APP_MODULE === 'employee';
  const storeSelector = useSelector((state: AppState) => state.storeSelector);
  const dispatch = useDispatch();
  const [isCollapsedStore, setIsCollapsedStore] = useState<boolean>(false);

  const toggleStore = (store: StoreAndAgencyInfo) => () => {
    dispatch(actions.storeSelector.toggleStore(store));
  };

  /**
   * 店舗選択のフィルターを全てクリアする
   * @returns void
   */
  const onClickClearSelectedStores = () => {
    // 営業モジュールでない場合(agenciesがreduxStore上に存在しない場合)は何もしない
    if (!storeSelector.agencies) {
      return;
    }
    // すでに全てクリア済みである場合は、dispatchしない
    if (storeSelector.selectedStores.length === 0) {
      return;
    }
    dispatch(actions.storeSelector.clearSelectedStores());
  };

  /**
   * 店舗選択のフィルターを全て選択する
   * @returns void
   */
  const onClickSelectAllStores = () => {
    // 営業モジュールでない場合(agenciesがreduxStore上に存在しない場合)は何もしない
    if (!storeSelector.agencies) {
      return;
    }
    // すでに全て選択済みである場合は、dispatchしない
    if (storeSelector.selectedStores.length === storeSelector.modalSelectedStores.length) {
      return;
    }
    dispatch(actions.storeSelector.selectAllStores());
  };

  /**
   * 担当者である店舗のみを選択する
   * @returns void
   */
  const onClickSelectOnlyStoreInCharge = () => {
    // 営業モジュールでない場合(agenciesがreduxStore上に存在しない場合)は何もしない
    if (!storeSelector.agencies) {
      return;
    }
    // agenciesより、担当店舗を取得します
    const storesIsInCharge: string[] = [];
    for (const agencyCd of Object.keys(storeSelector.agencies)) {
      if (storeSelector.agencies[agencyCd].isStoreInCharge) {
        storesIsInCharge.push(agencyCd);
      }
    }
    // すでに担当店舗のみを選択済みである場合は、は何もしない
    // 非破壊でsortした結果を比較し一致を確認します
    if (storeSelector.selectedStores.slice().sort().toString() === storesIsInCharge.slice().sort().toString()) {
      return;
    }
    // reduxの状態を更新する()
    dispatch(actions.storeSelector.selectOnlyStoresInCharge());
  };

  return (
    <>
      <hr className='uc-divider u-mb8 u-mhn16' />
      <div className='uc-form-field u-mt24'>
        <div className='uc-form-field__label　u-float-left u-mb16'>
          <span
            data-testid='filter-linked-data-drawer-open-collapsed'
            data-testid-e2e='filter-linked-data-drawer-open-collapsed'
            className='filter-drawer__collapse'
            onClick={() => setIsCollapsedStore((prev) => !prev)}>
            <Icon className={classNames('filter-drawer__arrow-icon', isCollapsedStore && 'is-rotate')}>keyboard_arrow_up</Icon>
            <span className='u-text-middle u-text-bold'>表示する店舗</span>
          </span>
          {/* クリックして店舗選択モーダルを表示する */}
          <Button
            data-testid='filter-linked-data-drawer-open-storeSelector'
            data-testid-e2e='filter-linked-data-drawer-open-storeSelector'
            className='u-w5s u-float-right'
            type='transparent'
            height='s'
            leftIcon='store'
            label='店舗/店舗フィルタを選択'
            onClick={() => dispatch(actions.storeSelector.toggleModal())}
          />
        </div>

        <Collapse in={!isCollapsedStore}>
          <StoreFilterEmployeeSetting
            isEmployeeModule={isEmployeeModule}
            onClickSelectOnlyStoreInCharge={onClickSelectOnlyStoreInCharge}
            onClickClearSelectedStores={onClickClearSelectedStores}
            onClickSelectAllStores={onClickSelectAllStores}
          />

          <div className='uc-form-field__control'>
            <StoreTagsList storeSelector={storeSelector} toggleStore={toggleStore} />
          </div>
        </Collapse>
      </div>
    </>
  );
};

interface StoreFilterEmployeeSettingProps {
  isEmployeeModule: boolean;
  onClickSelectOnlyStoreInCharge: () => void;
  onClickClearSelectedStores: () => void;
  onClickSelectAllStores: () => void;
}

const StoreFilterEmployeeSetting = ({
  isEmployeeModule,
  onClickSelectOnlyStoreInCharge,
  onClickClearSelectedStores,
  onClickSelectAllStores,
}: StoreFilterEmployeeSettingProps) => {
  if (!isEmployeeModule) return null;
  return (
    <>
      <div className='clearfix u-mb8 u-mx8'>
        <div className='u-float-left'>
          <Button
            className='u-mx8'
            type='transparent'
            onClick={onClickSelectOnlyStoreInCharge}
            height='s'
            width='s'
            label='担当店舗のみ選択'
            data-testid='select-only-store-in-charge'
          />
          <Button
            className='u-mx8'
            type='transparent'
            onClick={onClickClearSelectedStores}
            height='s'
            width='2s'
            label='全てクリア'
            data-testid='clear-selected-stores'
          />
          <Button
            className='u-mx8'
            type='transparent'
            onClick={onClickSelectAllStores}
            height='s'
            width='2s'
            label='全て選択'
            data-testid='select-all-stores'
          />
        </div>
      </div>
      <hr className='uc-divider u-mb8 u-mhn16' />
    </>
  );
};

interface StoreTagsListProps {
  storeSelector: StoreSelectorState;
  toggleStore: (store: StoreAndAgencyInfo) => () => void;
}

const StoreTagsList = ({ storeSelector, toggleStore }: StoreTagsListProps) => {
  // 営業モジュールの場合
  if (storeSelector.agencies) {
    return (
      <>
        {[...Object.values(storeSelector.agencies)]
          // 店舗選択モーダルにて、選択済みの店舗のみを表示します
          .filter((item) => storeSelector.modalSelectedStores.includes(item.agencyCd))
          // 第一ソートキー:企業正式名称、第二ソートキー:取次店略称
          // にて並び替えて表示します
          .sort((a, b) => {
            for (const sortField of SORT_FIELDS) {
              const compare = String(a[sortField]).localeCompare(String(b[sortField]), 'ja');
              if (compare !== 0) return compare;
            }
            return a.agencyCd.localeCompare(b.agencyCd, 'ja');
          })
          .map((store) => (
            <Badge
              className='agency-store-tag u-mr8 u-mb8'
              activeClassName='agency-store-tag--active'
              isActive={store.selectedForFilter ?? false}
              onClick={toggleStore(store)}>
              {/* (企業名正式名称)取次店略称 の形式にて、値を表示します */}
              {getCompanyCfcDisplayName(String(store.companyCfc), store.shortName)}
            </Badge>
          ))}
      </>
    );
  }

  // 代理店モジュールの場合
  return (
    <>
      {storeSelector.sortedModalSelectedStores
        .map((agencyCd) => storeSelector.stores?.[agencyCd])
        .map(
          (store) =>
            store && (
              <Badge
                className='agency-store-tag u-mr8 u-mb8'
                activeClassName='agency-store-tag--active'
                isActive={store?.selectedForFilter ?? false}
                onClick={toggleStore(store)}>
                {getAgencyStoreShortName(store.shortName, store.agencyCd, String(store.companyCfc))}
              </Badge>
            ),
        )}
    </>
  );
};

export default LinkedDataStoreSelectModal;
